import React, { useEffect, useState } from 'react';
import { getEmployees, getDepartments } from '../services/employeeService';
import { getProjects } from '../services/projectService';
import {
  getSupervisorPerformance,
  getDepartmentPerformance,
  getProjectPerformance,
  getEmployeePerformance,
  getTopSupervisors,
  getTopDepartments,
  getTopProjects,
  getTopEmployees,
} from '../services/performanceService';
import { getInspectorPerformance } from '../services/qualityService';
import { exportPerformanceXlsx } from '../services/exportService';
import { downloadBlob } from '../utils/downloadFile';
import { formatDateDDMMYY } from '../utils/formatDate';
import useUIStore from '../store/uiStore';
import useAuthStore from '../store/authStore';
import { isSalesMember } from '../utils/access';
import ScrollX from '../components/ScrollX';

const MODES = [
  { value: 'supervisor', label: 'By Supervisor' },
  { value: 'department', label: 'By Department' },
  { value: 'inspector', label: 'Quality Inspector' },
  { value: 'project', label: 'Project' },
  { value: 'employee', label: 'Employee' },
];

const money = (n) => `₹${Number(n || 0).toLocaleString()}`;

// Local (viewer's own) clock, not UTC - the browser already runs in the
// user's own timezone (IST for this company), unlike the server-side "today"
// bug fixed elsewhere in this app (see app/ist_time.py) which was about the
// *server's* clock defaulting to UTC.
const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => toISODate(new Date());
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
};

// Any column whose key ends in "_date" is auto-formatted DD-MM-YY unless
// the column supplies its own render - one place to keep every date
// column in this page's several tables consistent, instead of adding
// formatDateDDMMYY to each column definition individually.
const defaultCellValue = (row, c) => {
  const value = row[c.key];
  if (c.key.endsWith('_date') && value) return formatDateDDMMYY(value);
  return value ?? '—';
};

// footer, if given, is a {columnKey: displayValue} map for a bold totals row
// at the bottom - only rendered when there's at least one data row.
const Table = ({ columns, rows, empty, footer }) => (
  <div className="border border-gray-200 rounded">
    <ScrollX>
    <table className="w-full text-xs">
      <thead className="bg-gray-50">
        <tr>
          {columns.map((c) => (
            <th key={c.key} className="text-left px-3 py-2 font-semibold text-gray-600 whitespace-nowrap">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="px-3 py-4 text-center text-gray-400">
              {empty || 'No records in this range'}
            </td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <tr key={row.id ?? i}>
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 whitespace-nowrap max-w-xs truncate" title={row[c.key]}>
                  {c.render ? c.render(row) : defaultCellValue(row, c)}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
      {footer && rows.length > 0 && (
        <tfoot>
          <tr className="bg-gray-50 font-semibold text-gray-900 border-t border-gray-200">
            {columns.map((c) => (
              <td key={c.key} className="px-3 py-2 whitespace-nowrap">
                {footer[c.key] ?? ''}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
    </ScrollX>
  </div>
);

const SectionHeading = ({ children }) => (
  <h3 className="font-semibold text-gray-900 mt-6 mb-2">{children}</h3>
);

const sumField = (rows, key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);

// Tank (production-mode categories) and Pipeline (site-mode categories) work
// is kept in separate tables here rather than one combined list with a
// category column, since the whole point is telling the two apart at a
// glance - an "Uncategorized" table only appears if there's anything in it.
// costKey picks which cost column to total in the footer row - the whole
// crew's cost for a Project report, or just this one person's cost for an
// Employee report.
const ModeSplitWorkLog = ({ byMode, columns, costKey = 'total_salary_cost' }) => (
  <>
    {['production', 'site', 'uncategorized'].map((key) => {
      const rows = byMode?.[key] || [];
      if (key === 'uncategorized' && rows.length === 0) return null;
      const footer = rows.length > 0 ? {
        work_description: 'Total',
        hours_logged: sumField(rows, 'hours_logged'),
        [costKey]: money(sumField(rows, costKey)),
      } : null;
      return (
        <div key={key}>
          <SectionHeading>{MODE_LABELS[key]} Work Log</SectionHeading>
          <Table columns={columns} rows={rows} empty="No entries in this range" footer={footer} />
        </div>
      );
    })}
  </>
);

const StatCard = ({ value, label, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-50 text-gray-900',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
  };
  return (
    <div className={`rounded p-4 ${tones[tone]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
};

const PUNCH_POINT_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'punch_point_type', label: 'Type' },
  { key: 'issue_description', label: 'Description' },
  { key: 'severity', label: 'Severity' },
  {
    key: 'assigned_to',
    label: 'Assigned To',
    render: (r) => r.assigned_to_supervisor || r.assigned_to_department || '—',
  },
  { key: 'status', label: 'Status' },
  { key: 'created_date', label: 'Created' },
  { key: 'expected_completion_date', label: 'Expected' },
  { key: 'days_late', label: 'Days Late' },
];

const NCR_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'ncr_type', label: 'Type' },
  { key: 'description', label: 'Description' },
  { key: 'severity', label: 'Severity' },
  { key: 'disposition', label: 'Disposition' },
  { key: 'status', label: 'Status' },
  { key: 'raised_date', label: 'Raised' },
];

const WORKER_BREAKDOWN_COLUMNS = [
  { key: 'name', label: 'Worker' },
  { key: 'role', label: 'Role' },
  { key: 'days_present', label: 'Days Present' },
  { key: 'days_absent', label: 'Days Absent' },
  { key: 'total_hours', label: 'Hours' },
  { key: 'total_cost', label: 'Cost', render: (r) => money(r.total_cost) },
];

const DAILY_REPORT_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'category_name', label: 'Category' },
  { key: 'crew', label: 'Crew' },
  { key: 'work_description', label: 'Work Description' },
  { key: 'hours_logged', label: 'Hours' },
  { key: 'total_salary_cost', label: 'Cost (crew)', render: (r) => money(r.total_salary_cost) },
];

const EMPLOYEE_DAILY_REPORT_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'category_name', label: 'Category' },
  { key: 'role_on_report', label: 'Role' },
  { key: 'project', label: 'Project' },
  { key: 'work_description', label: 'Work Description' },
  { key: 'hours_logged', label: 'Hours' },
  { key: 'cost_to_employee', label: 'Cost (theirs)', render: (r) => money(r.cost_to_employee) },
];

const MODE_LABELS = {
  production: 'Tank / Manufacturing',
  site: 'Pipeline / Site',
  uncategorized: 'Uncategorized',
};

const DELAY_COLUMNS = [
  { key: 'id', label: 'ID' },
  {
    key: 'responsible',
    label: 'Responsible',
    render: (r) => r.responsible_supervisor || r.responsible_department || '—',
  },
  { key: 'expected_completion_date', label: 'Expected' },
  { key: 'actual_completion_date', label: 'Actual' },
  { key: 'days_late', label: 'Days Late' },
  { key: 'status', label: 'Status' },
];

// "Top 10" leaderboard shown the moment a mode is picked, before any one
// entity is selected - so the page isn't blank and the dropdown isn't the
// only way to find who/what to look at. One config per mode that has a
// backend /performance/top/<kind> endpoint (all but 'inspector', which is
// a small, separate leaderboard-shaped page in its own right - see
// getInspectorPerformance's usage above).
const LEADERBOARD_CONFIG = {
  supervisor: {
    fetcher: getTopSupervisors,
    valueKey: 'total_assigned',
    formatValue: (n) => n,
    subLabel: (item) => `${item.on_time_percentage}% on-time`,
  },
  department: {
    fetcher: getTopDepartments,
    valueKey: 'total_assigned',
    formatValue: (n) => n,
    subLabel: (item) => `${item.on_time_percentage}% on-time`,
  },
  // A function, not a plain object, because Project is the one leaderboard
  // whose ranking metric depends on the "Include expenses" toggle - all-in
  // cost (labor + material/vendor spend) instead of labor-only. See
  // getLeaderboardConfig below for how this and the plain-object entries
  // are both resolved.
  project: (includeExpenses) => ({
    fetcher: getTopProjects,
    valueKey: includeExpenses ? 'all_in_cost' : 'total_cost',
    formatValue: money,
    subLabel: (item) => includeExpenses
      ? `Labor ${money(item.total_cost)} + Expenses ${money(item.expense_cost)}`
      : `${item.total_man_hours} man-hrs · ${item.punch_point_count} punch points`,
  }),
  employee: {
    fetcher: getTopEmployees,
    valueKey: 'total_cost',
    formatValue: money,
    subLabel: (item) => `${item.days_present} days present`,
  },
};

// Most entries in LEADERBOARD_CONFIG are static; 'project' is a function of
// includeExpenses (see above). This resolves either shape uniformly.
const getLeaderboardConfig = (mode, includeExpenses) => {
  const entry = LEADERBOARD_CONFIG[mode];
  if (!entry) return null;
  return typeof entry === 'function' ? entry(includeExpenses) : entry;
};

// Bars are click-to-drill-down: picking one selects it and immediately loads
// the same full report the dropdown + "View Report" flow below produces, so
// the leaderboard doubles as a faster entity picker.
const Leaderboard = ({ items, config, onSelect }) => {
  const max = Math.max(1, ...items.map((it) => Number(it[config.valueKey]) || 0));
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={item.id} onClick={() => onSelect(item.id)} className="cursor-pointer group">
          <div className="flex justify-between items-baseline gap-2 text-xs mb-1">
            <span className="text-gray-700 font-medium group-hover:underline truncate">
              {i + 1}. {item.name}{item.department ? ` · ${item.department}` : ''}
            </span>
            <span className="font-semibold text-gray-900 whitespace-nowrap">{config.formatValue(item[config.valueKey])}</span>
          </div>
          <div className="bg-gray-100 rounded h-2.5">
            <div
              className="h-2.5 rounded bg-komal-blue group-hover:opacity-80"
              style={{ width: `${Math.max((Number(item[config.valueKey]) || 0) / max * 100, 2)}%` }}
            />
          </div>
          {config.subLabel && <div className="text-[11px] text-gray-400 mt-0.5">{config.subLabel(item)}</div>}
        </div>
      ))}
    </div>
  );
};

const INSPECTION_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'inspection_type', label: 'Type' },
  { key: 'inspection_date', label: 'Date' },
  { key: 'status', label: 'Status' },
  { key: 'findings', label: 'Findings' },
];

const PerformanceReportPage = () => {
  const { user } = useAuthStore();
  const [mode, setMode] = useState('supervisor');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  // Defaults to the last 30 days (inclusive of today) rather than the
  // backend's own current-calendar-month fallback, so the page - and its
  // Top 10 leaderboards - has recent data to show immediately. Once the
  // user picks their own dates, those are what's used everywhere below.
  const [startDate, setStartDate] = useState(() => daysAgoISO(29));
  const [endDate, setEndDate] = useState(() => todayISO());
  const [result, setResult] = useState(null);
  const [resultMode, setResultMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeSubProjects, setIncludeSubProjects] = useState(false);
  // Project-only: fold in material/vendor spend from the Expenses module
  // (approved SiteExpense + VendorBill) on top of labor cost, for a true
  // all-in project cost instead of labor-only.
  const [includeExpenses, setIncludeExpenses] = useState(false);
  const [topItems, setTopItems] = useState([]);
  const [topLoading, setTopLoading] = useState(false);
  const [topHidden, setTopHidden] = useState(false); // true when the current role isn't allowed to see this leaderboard (403) - hidden, not toasted, since that's an expected permission boundary for many roles
  const showToast = useUIStore((s) => s.showToast);

  useEffect(() => {
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    getDepartments().then((res) => setDepartments(res.data.departments || [])).catch(() => {});
    // 'all' statuses, not just active (2026-09-12 bug fix) - a completed/
    // on-hold/cancelled project still needs its cost/performance visible;
    // the default active-only fetch made it impossible to even select one
    // here at all (same bug class already fixed in MaterialPurchasesPage).
    getProjects('all').then((res) => setProjects(res.data.projects || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setSelectedId('');
    setResult(null);
    setIncludeSubProjects(false);
    setIncludeExpenses(false);
  }, [mode]);

  // Auto-loads the Top 10 leaderboard for whichever mode/date-range is
  // active, so the page has something to show before any one entity is
  // picked. A 403 (role not allowed to see this comparison - see
  // performance_routes.py's per-leaderboard scoping) hides the panel
  // quietly rather than showing an error toast for what's a normal,
  // expected boundary for many roles.
  useEffect(() => {
    const config = getLeaderboardConfig(mode, includeExpenses);
    if (!config) {
      setTopItems([]);
      setTopHidden(false);
      return;
    }
    let cancelled = false;
    setTopLoading(true);
    setTopHidden(false);
    config.fetcher({ startDate: startDate || undefined, endDate: endDate || undefined, includeExpenses })
      .then((res) => { if (!cancelled) setTopItems(res.data.items || []); })
      .catch(() => { if (!cancelled) setTopHidden(true); })
      .finally(() => { if (!cancelled) setTopLoading(false); });
    return () => { cancelled = true; };
  }, [mode, startDate, endDate, includeExpenses]);

  const handleFetch = async (idOverride) => {
    const id = typeof idOverride === 'string' || typeof idOverride === 'number' ? String(idOverride) : selectedId;
    if (!id) {
      showToast('Please select a ' + mode, 'error');
      return;
    }
    setLoading(true);
    try {
      let res;
      const range = { startDate: startDate || undefined, endDate: endDate || undefined };
      if (mode === 'supervisor') res = await getSupervisorPerformance(id, range);
      else if (mode === 'department') res = await getDepartmentPerformance(id, range);
      else if (mode === 'project') res = await getProjectPerformance(id, { ...range, includeSubProjects, includeExpenses });
      else if (mode === 'employee') res = await getEmployeePerformance(id, range);
      else res = await getInspectorPerformance(id);
      setResult(res.data);
      setResultMode(mode);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load performance data', 'error');
    }
    setLoading(false);
  };

  // Selecting a leaderboard entry both picks it in the dropdown (so the
  // rest of the page - export, date range, etc. - stays in sync) and loads
  // its report immediately, without a second click on "View Report".
  const handleSelectFromLeaderboard = (id) => {
    setSelectedId(String(id));
    handleFetch(id);
  };

  // Re-fetch automatically when either project-only toggle changes, so the
  // user doesn't have to click Fetch again just to flip one.
  useEffect(() => {
    if (mode === 'project' && selectedId && result) {
      handleFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeSubProjects, includeExpenses]);

  const handleExport = async () => {
    if (!selectedId || mode === 'inspector' || mode === 'project' || mode === 'employee') return;
    try {
      const res = await exportPerformanceXlsx(mode, selectedId);
      downloadBlob(res.data, `performance_${mode}_${selectedId}.xlsx`);
    } catch (err) {
      showToast('Failed to export', 'error');
    }
  };

  const options =
    mode === 'department' ? departments
    : mode === 'project' ? projects
    : mode === 'inspector' ? employees.filter((e) => e.role === 'Inspector')
    : employees; // supervisor, employee

  const entityLabel = {
    supervisor: 'supervisor',
    department: 'department',
    inspector: 'inspector',
    project: 'project',
    employee: 'employee',
  }[mode];

  if (isSalesMember(user)) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl font-bold text-white mb-2">Performance Report</h1>
          <p className="text-sm text-white/80">Performance reports are not available to the Sales team.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-6">Performance Report</h1>

        <div className="glass-card p-5 mb-6">
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            {MODES.map((m) => (
              <label key={m.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={mode === m.value}
                  onChange={() => setMode(m.value)}
                />
                {m.label}
              </label>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Select a {entityLabel}
              </label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Select a {entityLabel}</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            {mode !== 'inspector' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </>
            )}

            {mode === 'project' && (
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 pb-2 cursor-pointer" title="Adds approved material/vendor spend (Expenses module) on top of labor cost, for a true all-in project cost.">
                <input
                  type="checkbox"
                  checked={includeExpenses}
                  onChange={(e) => setIncludeExpenses(e.target.checked)}
                />
                Include expenses (all-in cost)
              </label>
            )}

            <button
              onClick={() => handleFetch()}
              disabled={loading}
              className="bg-komal-blue hover:bg-komal-blue-dark text-white text-sm font-semibold px-4 py-2 rounded disabled:opacity-60"
            >
              {loading ? 'Loading...' : 'View Report'}
            </button>
          </div>
        </div>

        {getLeaderboardConfig(mode, includeExpenses) && !topHidden && (
          <div className="glass-card p-5 mb-6">
            <h2 className="font-semibold text-gray-900 text-sm mb-1">
              Top 10 by {entityLabel} · {formatDateDDMMYY(startDate)} — {formatDateDDMMYY(endDate)}
              {mode === 'project' && includeExpenses ? ' · all-in cost' : ''}
            </h2>
            <p className="text-xs text-gray-400 mb-4">Click one to jump straight to its full report below.</p>
            {topLoading ? (
              <p className="text-xs text-gray-400">Loading...</p>
            ) : topItems.length === 0 ? (
              <p className="text-xs text-gray-400">No data in this range yet.</p>
            ) : (
              <Leaderboard items={topItems} config={getLeaderboardConfig(mode, includeExpenses)} onSelect={handleSelectFromLeaderboard} />
            )}
          </div>
        )}

        {result && (resultMode === 'supervisor' || resultMode === 'department') && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">
                {formatDateDDMMYY(result.start_date)} — {formatDateDDMMYY(result.end_date)}
              </h2>
              <button onClick={handleExport} className="text-komal-blue hover:underline text-xs font-semibold">
                Export Excel
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard value={result.total_punch_points_assigned} label="Total Assigned" />
              <StatCard value={result.completed_on_time} label="Completed On Time" tone="green" />
              <StatCard value={result.completed_late} label="Completed Late" tone="red" />
              <StatCard value={`${result.on_time_percentage}%`} label="On-Time %" tone="blue" />
              {result.still_open !== undefined && (
                <StatCard value={result.still_open} label="Still Open" tone="amber" />
              )}
              {result.avg_days_late !== undefined && (
                <StatCard value={result.avg_days_late} label="Avg Days Late" />
              )}
            </div>

            {resultMode === 'department' && result.delays && (
              <>
                <SectionHeading>Delays</SectionHeading>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <StatCard value={result.delay_count} label="Total Delays" />
                  <StatCard value={result.delays_open} label="Open" tone="amber" />
                  <StatCard value={result.delays_escalated} label="Escalated" tone="red" />
                  <StatCard value={result.delays_closed} label="Closed" tone="green" />
                </div>
                <Table columns={DELAY_COLUMNS} rows={result.delays} empty="No delays in this range" />
              </>
            )}
          </div>
        )}

        {result && resultMode === 'inspector' && (
          <div className="glass-card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">{result.month} Performance</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard value={result.total_created} label="Punch Points Created" />
              <StatCard value={result.total_verified} label="Verified" tone="green" />
              <StatCard value={result.pending_verification} label="Pending Verification" tone="amber" />
              <StatCard value={`${result.severity_accuracy_percentage}%`} label="Severity Accuracy" tone="blue" />
              <StatCard value={result.avg_verification_days} label="Avg Verification Days" />
              <div className="bg-gray-50 rounded p-4">
                <div className="text-xs text-gray-500 mb-1">Severity Breakdown</div>
                <div className="text-xs">
                  Low {result.severity_breakdown?.low ?? 0} · Medium {result.severity_breakdown?.medium ?? 0} · High {result.severity_breakdown?.high ?? 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {result && resultMode === 'project' && (
          <div className="glass-card p-6">
            <h2 className="font-semibold text-gray-900 mb-1">{result.project?.name}</h2>
            <p className="text-xs text-gray-500 mb-4">
              {result.project?.client_name} · {formatDateDDMMYY(result.start_date)} — {formatDateDDMMYY(result.end_date)}
            </p>

            {result.sub_projects && result.sub_projects.length > 0 && (
              <div className="bg-blue-50 border border-blue-100 rounded p-3 mb-4 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-blue-800">
                  This project has {result.sub_projects.length} sub-project{result.sub_projects.length === 1 ? '' : 's'}:{' '}
                  {result.sub_projects.map((sp) => sp.name).join(', ')}
                </div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-blue-800 whitespace-nowrap cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeSubProjects}
                    onChange={(e) => setIncludeSubProjects(e.target.checked)}
                  />
                  Include sub-projects in totals below
                </label>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {result.totals.includes_expenses ? (
                <>
                  <StatCard value={money(result.totals.total_cost)} label="Labor Cost" />
                  {/* Broken out 2026-09-12 - a single merged "Site/Vendor/
                      Material Expenses" figure made it impossible to see
                      how much (if any) was material, which read as
                      "material cost is missing" even when it was already
                      folded into the total. */}
                  <StatCard value={money(result.totals.site_expense_cost)} label="Site Expenses" tone="amber" />
                  <StatCard value={money(result.totals.vendor_expense_cost)} label="Vendor Expenses" tone="amber" />
                  <StatCard value={money(result.totals.material_expense_cost)} label="Material Expenses" tone="amber" />
                  <StatCard value={money(result.totals.all_in_cost)} label="All-In Cost (range)" tone="blue" />
                </>
              ) : (
                <StatCard value={money(result.totals.total_cost)} label="Labor Cost (range)" tone="blue" />
              )}
              <StatCard value={result.totals.total_man_hours} label="Total Man-Hours" />
              <StatCard value={result.punch_points.length} label="Punch Points" />
              <StatCard value={result.ncrs.length} label="NCRs" tone="red" />
            </div>
            {!result.totals.includes_expenses && (
              <p className="text-xs text-gray-400 -mt-2 mb-2">
                Labor cost only. Tick "Include expenses" above to see site/vendor/material spend broken out and an all-in cost.
              </p>
            )}

            <SectionHeading>Tank vs Pipeline</SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard value={money(result.totals.tank_cost)} label="Tank Cost" />
              <StatCard value={result.totals.tank_man_hours} label="Tank Man-Hours" />
              <StatCard value={money(result.totals.pipeline_cost)} label="Pipeline Cost" tone="amber" />
              <StatCard value={result.totals.pipeline_man_hours} label="Pipeline Man-Hours" tone="amber" />
            </div>

            <SectionHeading>Workers</SectionHeading>
            <p className="text-xs text-gray-500 -mt-1 mb-2">
              Who actually did the work (crew), not just who filed the reports.
            </p>
            <Table
              columns={WORKER_BREAKDOWN_COLUMNS}
              rows={result.worker_breakdown}
              empty="No crew logged against this project in this range"
            />

            <SectionHeading>Punch Points</SectionHeading>
            <Table columns={PUNCH_POINT_COLUMNS} rows={result.punch_points} />

            <SectionHeading>Delays</SectionHeading>
            <Table columns={DELAY_COLUMNS} rows={result.delays} />

            <SectionHeading>NCRs</SectionHeading>
            <Table columns={NCR_COLUMNS} rows={result.ncrs} />

            <SectionHeading>Inspections</SectionHeading>
            <Table columns={INSPECTION_COLUMNS} rows={result.inspections} />

            <ModeSplitWorkLog byMode={result.daily_reports_by_mode} columns={DAILY_REPORT_COLUMNS} />
          </div>
        )}

        {result && resultMode === 'employee' && (
          <div className="glass-card p-6">
            <h2 className="font-semibold text-gray-900 mb-1">{result.employee?.name}</h2>
            <p className="text-xs text-gray-500 mb-4">
              {result.employee?.role} · {result.employee?.department} · {formatDateDDMMYY(result.start_date)} — {formatDateDDMMYY(result.end_date)}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard value={result.totals.total_hours} label="Total Hours" />
              <StatCard value={money(result.totals.total_cost)} label="Total Cost" tone="blue" />
              <StatCard value={result.totals.punch_point_count} label="Punch Points Created" />
              <StatCard value={result.totals.ncr_count} label="NCRs Raised" tone="amber" />
              <StatCard value={result.totals.delay_count} label="Delays" tone="red" />
            </div>

            <SectionHeading>Tank vs Pipeline</SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard value={money(result.totals.tank_cost)} label="Tank Cost" />
              <StatCard value={result.totals.tank_man_hours} label="Tank Hours" />
              <StatCard value={money(result.totals.pipeline_cost)} label="Pipeline Cost" tone="amber" />
              <StatCard value={result.totals.pipeline_man_hours} label="Pipeline Hours" tone="amber" />
            </div>

            <ModeSplitWorkLog
              byMode={result.daily_reports_by_mode}
              columns={EMPLOYEE_DAILY_REPORT_COLUMNS}
              costKey="cost_to_employee"
            />

            <SectionHeading>Delays</SectionHeading>
            <p className="text-xs text-gray-500 -mt-1 mb-2">
              Punch points assigned to this person that were verified late.
            </p>
            <Table columns={DELAY_COLUMNS} rows={result.delays} empty="No delays in this range" />

            <SectionHeading>Punch Points Created</SectionHeading>
            <Table columns={PUNCH_POINT_COLUMNS} rows={result.punch_points_created} />

            <SectionHeading>Punch Points Assigned</SectionHeading>
            <Table columns={PUNCH_POINT_COLUMNS} rows={result.punch_points_assigned} />

            <SectionHeading>Punch Points Verified</SectionHeading>
            <Table columns={PUNCH_POINT_COLUMNS} rows={result.punch_points_verified} />

            <SectionHeading>NCRs Raised</SectionHeading>
            <Table columns={NCR_COLUMNS} rows={result.ncrs_raised} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceReportPage;
